import {API} from 'common/api';
import type {TakeHomeDetail, TakeHomeListItem, GradeTakehomeResponse, UserSolution, TopSolution} from 'iq-takehomes/types';

type TakehomeFileMeta = {
  filename: string;
  size: number;
  contentType: string;
  s3Key: string;
};

// C narrows the per-file category to a caller's union (e.g. interview-experience
// take-home files), defaulting to string for callers that don't constrain it.
type TakehomeFileCategory<C extends string = string> = {
  filename: string;
  category: C;
};

type TakehomeValidationResult<C extends string = string> = {
  valid: boolean;
  feedback: string;
  fileCategories: Array<TakehomeFileCategory<C>>;
};

type TakeHomeQuota = {
  used: number;
  total: number;
};

// getQuota fetches the caller's take-home quota from the backend.
// Returns null when the backend returns 204 (Premium/Admin users, or no fingerprint).
const getQuota = async (): Promise<TakeHomeQuota | null> => {
  const result = (await API.get('/v1/takehomes-quota')) as TakeHomeQuota | Record<string, never>;
  if (!('used' in result)) return null;
  return result as TakeHomeQuota;
};

const listTakeHomes = async (): Promise<TakeHomeListItem[]> => {
  const result = await API.get('/v1/takehomes');
  return (result as TakeHomeListItem[]) ?? [];
};

const getTakeHome = async (slug: string): Promise<TakeHomeDetail | null> => {
  const result = (await API.get(`/v1/takehomes/${slug}`)) as TakeHomeDetail & {error?: string};
  if (result?.error) return null;
  return result;
};

// Admin-only. Loads any take-home (published or draft) as the public detail shape,
// always fully-unlocked, so admins can preview it before publishing. Backend gates
// this with requireAdmin.
const getTakeHomePreview = async (id: number): Promise<TakeHomeDetail | null> => {
  const result = (await API.get(`/v1/admin/takehomes/${id}/preview`)) as TakeHomeDetail & {error?: string};
  if (result?.error) return null;
  return result;
};

// slug is passed only for solution uploads, routing the file to
// takehomes/solutions/<slug>/. Assignment shares omit it.
const getTakehomeUploadUrl = async (
  filename: string,
  contentType: string,
  submissionFlowId: string,
  slug?: string,
): Promise<{uploadUrl: string; s3Key: string; fields: Record<string, string>}> => {
  const response = await API.post('/v1/takehomes/upload-url', {
    filename,
    content_type: contentType,
    submission_flow_id: submissionFlowId,
    ...(slug ? {slug} : {}),
  });
  return {
    uploadUrl: response.upload_url as string,
    s3Key: response.s3_key as string,
    fields: (response.fields ?? {}) as Record<string, string>,
  };
};

const validateTakehomeFiles = async <C extends string = string>(
  files: Array<TakehomeFileMeta>,
): Promise<TakehomeValidationResult<C>> => {
  const response = await API.post('/v1/takehomes/validate', {
    files: files.map((f) => ({
      filename: f.filename,
      size: f.size,
      content_type: f.contentType,
      s3_key: f.s3Key,
    })),
  });
  return {
    valid: response?.valid === true,
    feedback: response?.feedback ?? '',
    fileCategories: (response?.file_categories as Array<TakehomeFileCategory<C>>) ?? [],
  };
};

type SubmittedTakehomeFile = {
  filename: string;
  s3Key: string;
  category: string;
};

const submitTakeHome = async (
  submissionFlowId: string,
  files: Array<SubmittedTakehomeFile>,
): Promise<void> => {
  await API.post('/v1/takehomes/submit', {
    submission_flow_id: submissionFlowId,
    files: files.map((f) => ({filename: f.filename, s3_key: f.s3Key, category: f.category})),
  });
};

const gradeTakeHome = async (
  slug: string,
  s3Key: string,
  submissionFlowId: string,
): Promise<GradeTakehomeResponse> => {
  return API.post(`/v1/takehomes/grade/${slug}`, {
    s3_key: s3Key,
    submission_flow_id: submissionFlowId,
  }) as Promise<GradeTakehomeResponse>;
};

const getMyLatestSolution = async (slug: string): Promise<UserSolution | null> => {
  const result = await API.get(`/v1/takehomes/${slug}/my-solution`) as UserSolution & {error?: string};
  if (result?.error) return null;
  return result;
};

const getSolution = async (slug: string, solutionId: number): Promise<UserSolution | null> => {
  const result = await API.get(`/v1/takehomes/${slug}/solutions/${solutionId}`) as UserSolution & {error?: string};
  if (result?.error) return null;
  return result;
};

const getTopSolutions = async (slug: string): Promise<Array<TopSolution>> => {
  const result = await API.get(`/v1/takehomes/${slug}/solutions?limit=5`);
  return Array.isArray(result) ? (result as Array<TopSolution>) : [];
};

const listSolutions = async (slug: string): Promise<Array<TopSolution>> => {
  const result = await API.get(`/v1/takehomes/${slug}/solutions`);
  return Array.isArray(result) ? (result as Array<TopSolution>) : [];
};

export {listTakeHomes, getTakeHome, getTakeHomePreview, getTakehomeUploadUrl, validateTakehomeFiles, submitTakeHome, gradeTakeHome, getMyLatestSolution, getSolution, getTopSolutions, listSolutions, getQuota};
export type {TakehomeFileMeta, TakehomeValidationResult, SubmittedTakehomeFile, TakeHomeQuota};
